import type { FirebaseOptions } from 'firebase/app';

export type FirebasePublicConfig = Required<
  Pick<
    FirebaseOptions,
    'apiKey' | 'authDomain' | 'projectId' | 'storageBucket' | 'messagingSenderId' | 'appId'
  >
>;

const PLACEHOLDER_MARKERS = ['replace-with', 'your-', 'example', 'placeholder'];

const REQUIRED_FIREBASE_ENV: Array<{
  envName: string;
  configKey: keyof FirebasePublicConfig;
}> = [
  { envName: 'REACT_APP_FIREBASE_API_KEY', configKey: 'apiKey' },
  { envName: 'REACT_APP_FIREBASE_AUTH_DOMAIN', configKey: 'authDomain' },
  { envName: 'REACT_APP_FIREBASE_PROJECT_ID', configKey: 'projectId' },
  { envName: 'REACT_APP_FIREBASE_STORAGE_BUCKET', configKey: 'storageBucket' },
  { envName: 'REACT_APP_FIREBASE_MESSAGING_SENDER_ID', configKey: 'messagingSenderId' },
  { envName: 'REACT_APP_FIREBASE_APP_ID', configKey: 'appId' },
];

const isInvalidFirebaseEnvValue = (rawValue: string | undefined): boolean => {
  const value = rawValue?.trim();
  if (!value) return true;

  const lowerValue = value.toLowerCase();
  return PLACEHOLDER_MARKERS.some((marker) => lowerValue.includes(marker));
};

export const validateFirebasePublicConfig = (
  config: Partial<FirebasePublicConfig>
): string[] => {
  const invalidKeys: string[] = [];

  for (const item of REQUIRED_FIREBASE_ENV) {
    if (isInvalidFirebaseEnvValue(config[item.configKey])) {
      invalidKeys.push(item.envName);
    }
  }

  return invalidKeys;
};

export const createFirebasePublicConfig = (
  env: Record<string, string | undefined>
): FirebasePublicConfig => {
  const config = {
    apiKey: env.REACT_APP_FIREBASE_API_KEY,
    authDomain: env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.REACT_APP_FIREBASE_APP_ID,
  };

  const invalidKeys = validateFirebasePublicConfig(config);
  if (invalidKeys.length > 0) {
    throw new Error(
      `Firebase public web config is missing or still uses placeholder values: ${invalidKeys.join(
        ', '
      )}. Copy the public web app config from Firebase Console into an ignored local env file.`
    );
  }

  return Object.freeze(config) as FirebasePublicConfig;
};

export const __firebaseConfigInternals = {
  isInvalidFirebaseEnvValue,
  REQUIRED_FIREBASE_ENV,
};
